import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  CSSProperties,
  useCallback,
  memo,
} from 'react';
import {
  Button,
  Col,
  Empty,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import { DualAxes } from '@ant-design/plots';
import { Filters, FiltersCard } from '@components/Filters';
import { FilterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { IObject } from '@utils/defaultTypes';
import { CreateAlarmButton } from '@components/CreateAlarmButton/CreateAlarmButton';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import { TransactionsPayGraphTypes } from '@utils/alarmsConstants';
import { useGraphColors } from '@hooks/useGraphColors';
import { IOption } from '..';
import * as S from './styles';

interface ICharDataFormatted {
  status: string;
  value: number;
  date: string;
}

interface ITags {
  appname: string;
  status: string;
}

interface ISeries {
  values: ICharValues;
  tags: ITags;
}

interface LegendItem {
  name: string;
  color: string;
  value: number | string;
}

interface CustomLegendProps {
  items: LegendItem[];
  appname: string;
  onToggle: (appname: string, series: string) => void;
  visibleSeries: Record<string, Record<string, boolean>>;
}

type ICharValues = [timestamp: string, total: number | null][];

interface ICharDataResponse {
  results?: {
    series?: ISeries[];
  }[];
}

type GroupedItem = {
  appname: string;
  data: {
    status: string;
    value: number;
    date: string;
  }[];
};

interface IStyleCss {
  [key: string]: CSSProperties;
}

const FIRST_INDEX = 0;
const SECOND_INDEX = 1;
const MIN_VALUE = 0;

const DEFAULT_FORM_VALUE = {
  groupByTime: '10m',
  lastTime: '3h',
};

const SUCCESS_STATUS = 'SUCCESS';

const LEGENDS = {
  success: 'success',
  failure: 'failure',
  averageTime: 'averageTime',
};

const { Paragraph } = Typography;

interface IProps {
  options: {
    lastTimeOptions: IOption[];
    groupedOptions: IOption[];
  };
  setSearchParams: (values: IObject) => void;
  searchParams: IObject;
  getOptionsValue: (searchParam: string, defaultValue: string) => string;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  items,
  appname,
  onToggle,
  visibleSeries,
}) => {
  const { t } = useTranslation(['transaction-by-type']);
  const styles: IStyleCss = {
    customLegend: {
      marginTop: '12px',
      padding: '8px',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
      cursor: 'pointer',
    },
    colorMarker: {
      width: '16px',
      height: '16px',
      marginRight: '8px',
      borderRadius: '2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  return (
    <div style={styles.customLegend}>
      {items.map(item => (
        <div
          key={item.name}
          style={{
            ...styles.legendItem,
            opacity: visibleSeries[appname]?.[item.name] ? 1 : 0.5,
          }}
          onClick={() => onToggle(appname, item.name)}
        >
          <div
            style={{
              ...styles.colorMarker,
              backgroundColor: item.color,
            }}
          >
            <FilterOutlined style={{ color: 'black' }} />
          </div>
          <div>
            {t(`transaction-by-type:legends.${item.name}`)}
            {': '}
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const ChartComponent = memo(
  ({
    value,
    visibleSeries,
    getFilteredData,
    getLatestDataByStatus,
    t,
    getChartTitle,
    onToggleSeries,
    onOpenCreateAlarmModal,
    currentLanguage,
    colors,
  }: {
    value: GroupedItem;
    visibleSeries: Record<string, Record<string, boolean>>;
    getFilteredData: (
      data: ICharDataFormatted[],
      appname: string,
    ) => ICharDataFormatted[];
    getLatestDataByStatus: (
      status: string,
      data: ICharDataFormatted[],
      allData?: ICharDataFormatted[],
    ) => ICharDataFormatted | { value: number };
    t: (key: string, options?: any) => string;
    getChartTitle: (appname: string) => string;
    onToggleSeries: (appname: string, series: string) => void;
    onOpenCreateAlarmModal: (appName: string) => void;
    currentLanguage: string;
    colors: string[];
  }) => {
    const styles: IStyleCss = {
      chartHeaderContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
      },
      chartTitle: {
        width: '100%',
        textAlign: 'left',
        fontWeight: '600',
        marginBottom: '0',
        fontSize: '1rem',
      },
    };

    const getChartValueFormatted = (chartValue: number) => {
      const conversionFactor = 1000;
      const decimal = 2;
      if (chartValue < conversionFactor)
        return `${chartValue.toFixed(decimal)} ms`;
      return `${(chartValue / conversionFactor).toFixed(decimal)} s`;
    };

    const allBarData = value?.data.filter(
      item => item?.status !== LEGENDS.averageTime,
    );
    const allLineData = value?.data.filter(
      item => item.status === LEGENDS.averageTime,
    );

    if (!visibleSeries[value.appname]) {
      return null;
    }

    const barData = getFilteredData(allBarData, value.appname);
    const lineData = getFilteredData(allLineData, value.appname);

    const config: any = {
      data: [barData, lineData],
      xField: 'date',
      yField: ['value', 'value'],
      geometryOptions: [
        {
          geometry: 'column',
          seriesField: 'status',
          isStack: true,
          color: ({ status }: { status: string }) => {
            if (status === LEGENDS.failure) return colors[0];
            if (status === LEGENDS.success) return colors[1];
            return colors[2];
          },
        },
        {
          geometry: 'line',
          seriesField: 'status',
          color: ({ status }: { status: string }) => {
            if (status === LEGENDS.averageTime) return colors[2];
            return colors[2];
          },
          isArea: true,
          style: { fillOpacity: 0.3 },
        },
      ],
      yAxis: [
        {
          title: { text: 'Quantidade' },
        },
        {
          title: { text: 'Tempo médio' },
        },
      ],
      xAxis: {
        title: null,
        tickCount: 5,
        label: {
          formatter: (text: string) => {
            const date = new Date(text);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');

            return currentLanguage === 'en'
              ? `${month}/${day}`
              : `${day}/${month}`;
          },
        },
      },
      tooltip: {
        formatter: (datum: ICharDataFormatted) => {
          const date = new Date(datum?.date);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');

          const formattedDate =
            currentLanguage === 'en'
              ? `${month}/${day} ${hours}:${minutes}`
              : `${day}/${month} ${hours}:${minutes}`;

          const displayValue =
            datum?.status === LEGENDS.averageTime
              ? getChartValueFormatted(datum?.value)
              : datum?.value;

          return {
            name: t(`transaction-by-type:legends.${datum?.status}`),
            title: formattedDate,
            value: displayValue,
          };
        },
      },
      legend: {
        itemName: {
          formatter: (text: string) => {
            return t(`transaction-by-type:legends.${text}`);
          },
        },
      },
    };

    return (
      <Col key={value.appname}>
        <div style={styles.chartHeaderContainer}>
          <Paragraph style={styles.chartTitle}>
            {getChartTitle(value?.appname)}
          </Paragraph>
          <RoleChecker
            role={
              rolesConstants.INSIGHTS_TRANSACTIONS_ALARM_MANAGE_CONFIGURATION
            }
          >
            <CreateAlarmButton
              tooltipPosition="leftTop"
              openCreateAlarmModal={
                () => onOpenCreateAlarmModal(value?.appname)
                // eslint-disable-next-line react/jsx-curly-newline
              }
            />
          </RoleChecker>
        </div>
        <DualAxes {...config} />
        <CustomLegend
          items={[
            {
              name: LEGENDS.failure,
              color: colors[0],
              value:
                getLatestDataByStatus(LEGENDS.failure, allBarData, allBarData)
                  ?.value || MIN_VALUE,
            },
            {
              name: LEGENDS.success,
              color: colors[1],
              value:
                getLatestDataByStatus(LEGENDS.success, allBarData, allBarData)
                  ?.value || MIN_VALUE,
            },
            {
              name: LEGENDS.averageTime,
              color: colors[2],
              value: getChartValueFormatted(
                getLatestDataByStatus(LEGENDS.averageTime, allLineData)
                  ?.value || MIN_VALUE,
              ),
            },
          ]}
          appname={value.appname}
          onToggle={onToggleSeries}
          visibleSeries={visibleSeries}
        />
      </Col>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.visibleSeries[prevProps.value.appname] ===
        nextProps.visibleSeries[prevProps.value.appname] &&
      prevProps.t === nextProps.t &&
      prevProps.currentLanguage === nextProps.currentLanguage &&
      prevProps.colors === nextProps.colors
    );
  },
);

const TransactionsByType: React.FC<IProps> = ({
  options,
  setSearchParams,
  searchParams,
  getOptionsValue,
}) => {
  const styles: IStyleCss = {
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    select: {
      width: '100%',
      maxWidth: '15rem',
    },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    formTitle: { margin: 0 },
    charTitle: {
      margin: '0 0 1rem',
      width: '100%',
      textAlign: 'center',
      fontWeight: '600',
    },
    notFoundMessage: {
      margin: '1rem auto 0',
      textAlign: 'center',
    },
    questionIcon: { color: '#fff' },
  };

  const [formData, setFormData] = useState({
    groupByTime: getOptionsValue(
      searchParams?.groupByTimeType,
      DEFAULT_FORM_VALUE?.groupByTime,
    ),
    lastTime: getOptionsValue(
      searchParams?.lastTimeType,
      DEFAULT_FORM_VALUE?.lastTime,
    ),
  });
  const { customersSwitch, selectedApp, graphThemeKey } =
    useContext(SessionContext);
  const [data, setData] = useState<GroupedItem[]>([]);
  const { t, i18n } = useTranslation(['transaction-by-type']);
  const currentLanguage = i18n.language;
  const [showFilters, setShowFilters] = useState(true);
  const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState(false);
  const [selectedAppName, setSelectedAppName] = useState<string>('');
  const [visibleSeries, setVisibleSeries] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const graphColors = useGraphColors();

  const fetchParams = useMemo(
    () => ({
      params: {
        produto: selectedApp?.toUpperCase(),
        lastTime: formData?.lastTime,
        groupByTime: formData?.groupByTime,
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [customersSwitch?.selected, selectedApp, formData],
  );

  const { data: transactionsData, isLoading: transactionsIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/transacao/status_appnome',
      fetchParams,
    );

  const { data: averageTimeData, isLoading: averageTimeIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/transacao/status/tempo_medio',
      fetchParams,
    );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupedData = transactionsData?.results?.[
          FIRST_INDEX
        ]?.series?.reduce((acc: GroupedItem[], cur) => {
          const hasAppname = acc?.find(
            group => group?.appname === cur?.tags?.appname,
          );
          const statusName =
            cur?.tags?.status === SUCCESS_STATUS
              ? LEGENDS.success
              : LEGENDS.failure;
          if (!hasAppname) {
            const appnameGroup = {
              appname: cur?.tags?.appname,
              data: cur?.values?.map(curItem => ({
                status: statusName,
                value: curItem[SECOND_INDEX] || MIN_VALUE,
                date: curItem[FIRST_INDEX],
              })),
            };
            acc.push(appnameGroup);
          } else {
            hasAppname.data = [
              ...hasAppname.data,
              ...cur?.values?.map(curItem => ({
                status: statusName,
                value: curItem[SECOND_INDEX] || MIN_VALUE,
                date: curItem[FIRST_INDEX],
              })),
            ];
          }
          return acc;
        }, []);
        const result = groupedData?.map(({ appname, data: groupData }) => {
          const averageTime =
            averageTimeData?.results?.[FIRST_INDEX]?.series?.find(
              value => value?.tags?.appname === appname,
            )?.values || [];
          const averageTimeFormatted = averageTime?.map(averageTimeItem => ({
            status: LEGENDS.averageTime,
            value: averageTimeItem[SECOND_INDEX] || MIN_VALUE,
            date: averageTimeItem[FIRST_INDEX],
          }));
          return { appname, data: [...groupData, ...averageTimeFormatted] };
        });
        if (result?.length) {
          setData(result as GroupedItem[]);

          const initialVisibility: Record<string, Record<string, boolean>> = {};
          result.forEach(curr => {
            initialVisibility[curr.appname] = {
              [LEGENDS.success]: true,
              [LEGENDS.failure]: true,
              [LEGENDS.averageTime]: true,
            };
          });
          setVisibleSeries(initialVisibility);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };
    fetchData();
  }, [transactionsData, averageTimeData]);

  const filters = useMemo(
    () => [
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('transaction-by-type:labelPeriod'),
        element: (
          <Select
            onChange={lastTime => {
              setFormData(state => ({ ...state, lastTime }));
              setSearchParams({
                lastTimeType: lastTime,
              });
            }}
            value={getOptionsValue(
              searchParams?.lastTimeType,
              formData?.lastTime,
            )}
            options={options?.lastTimeOptions}
            style={styles.select}
          />
        ),
      },
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('transaction-by-type:labelGrouping'),
        element: (
          <Select
            onChange={groupByTime => {
              setFormData(state => ({ ...state, groupByTime }));
              setSearchParams({
                groupByTimeType: groupByTime,
              });
            }}
            options={options?.groupedOptions}
            value={getOptionsValue(
              searchParams?.groupByTimeType,
              formData?.groupByTime,
            )}
            style={styles.select}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData?.groupByTime, formData?.lastTime, options, t, searchParams],
  );

  const getFilteredData = useCallback(
    (originalData: ICharDataFormatted[], appname: string) => {
      if (!visibleSeries[appname]) return [];
      return originalData.filter(
        (item: ICharDataFormatted) => visibleSeries[appname][item.status],
      );
    },
    [visibleSeries],
  );

  const getLatestDataByStatus = useCallback(
    (
      status: string,
      charData: ICharDataFormatted[],
      allData?: ICharDataFormatted[],
    ): ICharDataFormatted | { value: number } => {
      const filteredData = charData?.filter(item => item.status === status);
      if (!filteredData.length) return { value: 0 };

      // Se allData foi fornecido, usa o último timestamp GLOBAL
      // Caso contrário, usa o último timestamp do próprio status
      let lastTimestamp: string;

      if (allData && allData.length > 0) {
        // Pega o último timestamp considerando TODAS as séries
        const allTimestamps = allData
          .map(item => item.date)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        lastTimestamp = allTimestamps[allTimestamps.length - 1];
      } else {
        // Fallback: usa o último timestamp do próprio status
        const sortedData = [...filteredData].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        lastTimestamp = sortedData[sortedData.length - 1].date;
      }

      // Pega TODOS os itens do último timestamp para este status e soma
      const lastItems = filteredData.filter(
        item => item.date === lastTimestamp,
      );

      if (lastItems.length === 0) {
        // Se não houver dados neste timestamp, retorna 0
        return { value: 0 };
      }

      if (lastItems.length === 1) {
        return lastItems[0];
      }

      // Se houver múltiplos itens no mesmo timestamp, soma os valores
      const totalValue = lastItems.reduce((sum, item) => sum + item.value, 0);
      return {
        ...lastItems[0],
        value: totalValue,
      };
    },
    [],
  );

  const getChartTitle = useCallback(
    (appname: string) => `${t('transactionsDashboardTitle')}: ${appname}`,
    [t],
  );

  const handleOpenFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const handleToggleSeries = useCallback((appname: string, series: string) => {
    setVisibleSeries(prev => {
      const newState = { ...prev };

      if (!newState[appname]) {
        return newState;
      }

      const appVisibility = { ...newState[appname] };

      const allHidden = Object.values(appVisibility).every(v => !v);
      const onlyThisVisible =
        appVisibility[series] &&
        Object.entries(appVisibility).filter(([k, v]) => k !== series && v)
          .length === 0;

      if (onlyThisVisible || allHidden) {
        newState[appname] = {
          [LEGENDS.success]: true,
          [LEGENDS.failure]: true,
          [LEGENDS.averageTime]: true,
        };
      } else if (appVisibility[series]) {
        newState[appname] = {
          [LEGENDS.success]: false,
          [LEGENDS.failure]: false,
          [LEGENDS.averageTime]: false,
          [series]: true,
        };
      } else {
        newState[appname] = {
          ...appVisibility,
          [series]: true,
        };
      }

      return newState;
    });
  }, []);

  const handleOpenCreateAlarmModal = useCallback((appName: string) => {
    setSelectedAppName(appName);
    setOpenCreateAlarmModal(true);
  }, []);

  const { isLoadingData, hasData } = useMemo(
    () => ({
      isLoadingData: transactionsIsLoading || averageTimeIsLoading,
      hasData: !!data?.length && Object.keys(visibleSeries).length > 0,
    }),
    [transactionsIsLoading, averageTimeIsLoading, data, visibleSeries],
  );

  return (
    <S.DashContainer>
      <Row gutter={[16, 16]}>
        <Col xl={24} xs={24}>
          <S.CardContainer
            title={
              <div className="card-header">
                <div className="card-title-box">
                  <div className="card-title">{t('title')}</div>
                  <Tooltip title={t('transaction-by-type:tooltipMessage')}>
                    <Button
                      type="link"
                      icon={
                        <QuestionCircleOutlined style={styles.questionIcon} />
                      }
                    />
                  </Tooltip>
                  <Filters isOpen={showFilters} onOpen={handleOpenFilters} />
                </div>
              </div>
            }
          >
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                {filters && (
                  <S.FiltersContainer>
                    <FiltersCard isOpen={showFilters} tableFilters={filters} />
                  </S.FiltersContainer>
                )}
              </Col>
              {(() => {
                if (isLoadingData) {
                  return (
                    <Col xs={24}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '400px',
                        }}
                      >
                        <Spin style={styles.spinner} />
                      </div>
                    </Col>
                  );
                }

                if (hasData) {
                  return (
                    <S.DashboardContainer>
                      <Row gutter={[16, 16]}>
                        {data.map(value => (
                          <Col
                            xs={24}
                            sm={24}
                            md={12}
                            lg={12}
                            xl={8}
                            xxl={6}
                            key={value.appname}
                          >
                            <S.DashboardCardContainer>
                              <ChartComponent
                                value={value}
                                visibleSeries={visibleSeries}
                                getFilteredData={getFilteredData}
                                getLatestDataByStatus={getLatestDataByStatus}
                                t={t}
                                getChartTitle={getChartTitle}
                                onToggleSeries={handleToggleSeries}
                                onOpenCreateAlarmModal={
                                  handleOpenCreateAlarmModal
                                }
                                currentLanguage={currentLanguage}
                                colors={graphColors[graphThemeKey]}
                              />
                            </S.DashboardCardContainer>
                          </Col>
                        ))}
                      </Row>
                    </S.DashboardContainer>
                  );
                }

                return (
                  <Col xs={24}>
                    <Empty
                      description={t('notFoundMessage')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Col>
                );
              })()}
            </Row>
          </S.CardContainer>
        </Col>
      </Row>
      <CreateAlarmSteps
        visible={openCreateAlarmModal}
        onClose={() => setOpenCreateAlarmModal(false)}
        preSelectedValues={{
          tipoGrafico: TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE,
          transactionByType: selectedAppName.toUpperCase(),
        }}
        readOnlyFields={['tipoGrafico', 'transactionByType']}
      />
    </S.DashContainer>
  );
};

export default TransactionsByType;
